import { motion } from "motion/react";
import type { ReactNode } from "react";
import { PEOPLE, type PersonId } from "../../data/people";
import { spring } from "../../lib/motion";
import { cn } from "../../lib/utils";
import type { Status } from "../../types";

export const STATUS: Record<
  Status,
  { label: string; dot: string; badge: string; soft: string; text: string; border: string; hex: string }
> = {
  open: { label: "Свободная", dot: "bg-muted-foreground", badge: "bg-muted text-muted-foreground border-border", soft: "bg-muted", text: "text-muted-foreground", border: "border-border", hex: "#a3a3a3" },
  wait: { label: "Ожидает", dot: "bg-wait", badge: "bg-wait-soft text-wait-fg border-wait-bd", soft: "bg-wait-soft", text: "text-wait-fg", border: "border-wait-bd", hex: "#f59e0b" },
  work: { label: "В работе", dot: "bg-work", badge: "bg-work-soft text-work-fg border-work-bd", soft: "bg-work-soft", text: "text-work-fg", border: "border-work-bd", hex: "#3b82f6" },
  review: { label: "Проверка", dot: "bg-review", badge: "bg-review-soft text-review-fg border-review-bd", soft: "bg-review-soft", text: "text-review-fg", border: "border-review-bd", hex: "#8b5cf6" },
  rework: { label: "Доработка", dot: "bg-rework", badge: "bg-rework-soft text-rework-fg border-rework-bd", soft: "bg-rework-soft", text: "text-rework-fg", border: "border-rework-bd", hex: "#ef4444" },
  publish: { label: "Публикация", dot: "bg-publish", badge: "bg-publish-soft text-publish-fg border-publish-bd", soft: "bg-publish-soft", text: "text-publish-fg", border: "border-publish-bd", hex: "#0d9488" },
  done: { label: "Выполнено", dot: "bg-done", badge: "bg-done-soft text-done-fg border-done-bd", soft: "bg-done-soft", text: "text-done-fg", border: "border-done-bd", hex: "#22c55e" },
};

export function StatusBadge({ status, children, className }: { status: Status; children?: ReactNode; className?: string }) {
  const s = STATUS[status];
  return (
    <span className={cn("inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border pl-2 pr-2.5 text-xs font-medium", s.badge, className)}>
      <span className={cn("size-1.5 rounded-full", s.dot)} />
      {children ?? s.label}
    </span>
  );
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex h-[22px] shrink-0 items-center rounded-full bg-secondary px-[9px] text-xs font-medium text-secondary-foreground", className)}>
      {children}
    </span>
  );
}

/** Пастельный фон аватара без фото — по id, чтобы у человека он был всегда один. */
const INITIAL_BG = ["#fde68a", "#bfdbfe", "#ddd6fe", "#fecaca", "#bbf7d0", "#fbcfe8", "#c7d2fe", "#fed7aa"];

export function Avatar({ id, size = 28, className, ring }: { id: PersonId; size?: number; className?: string; ring?: boolean }) {
  const p = PEOPLE[id];
  if (!p.avatar) {
    const letters = p.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
    const bg = INITIAL_BG[[...id].reduce((a, ch) => a + ch.charCodeAt(0), 0) % INITIAL_BG.length];
    return (
      <span
        title={p.name}
        style={{ width: size, height: size, background: bg, fontSize: Math.max(9, Math.round(size * 0.38)) }}
        className={cn("inline-grid shrink-0 select-none place-items-center rounded-full font-semibold text-foreground/70", ring && "ring-2 ring-background", className)}
      >
        {letters}
      </span>
    );
  }
  return (
    <img
      src={p.avatar}
      alt={p.name}
      title={p.name}
      draggable={false}
      style={{ width: size, height: size }}
      className={cn("shrink-0 rounded-full bg-secondary object-cover", ring && "ring-2 ring-background", className)}
    />
  );
}

export function CountPill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-background px-1.5 text-xs font-semibold text-muted-foreground tabular-nums", className)}>
      {children}
    </span>
  );
}

/** Счётчик, который «перекатывается» при изменении (M2) */
export function Ticker({ value, className }: { value: number | string; className?: string }) {
  return (
    <span className={cn("relative inline-flex overflow-hidden tabular-nums", className)}>
      <motion.span
        key={String(value)}
        initial={{ y: "-70%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={spring.snappy}
        className="inline-block"
      >
        {value}
      </motion.span>
    </span>
  );
}

/** Сегментированный переключатель: белая «таблетка» переезжает между пунктами */
export function Segmented<T extends string>({
  id,
  value,
  onChange,
  options,
  size = "md",
  className,
}: {
  id: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; count?: number | string; icon?: ReactNode }[];
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div className={cn("inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted p-1", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "relative inline-flex items-center gap-1.5 rounded-full font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/40",
              size === "md" ? "h-8 px-3 text-[13px]" : "h-7 px-2.5 text-xs",
              active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {active && (
              <motion.span
                layoutId={`seg-${id}`}
                transition={spring.snappy}
                className="absolute inset-0 rounded-full bg-background shadow-mxs"
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5 [&_svg]:size-3.5">
              {o.icon}
              {o.label}
              {o.count !== undefined && <span className="text-xs text-muted-foreground tabular-nums">{o.count}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function FilterChip({ active, onClick, children, count }: { active: boolean; onClick: () => void; children: ReactNode; count?: number }) {
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.96 }}
      transition={spring.snappy}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-medium transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted",
      )}
    >
      {children}
      {count !== undefined && <span className={cn("text-xs tabular-nums", active ? "text-ring" : "text-muted-foreground")}>{count}</span>}
    </motion.button>
  );
}
